/* eslint-disable no-console */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import {
  Checkbox,
  RadioBtn,
  DateInput,
  DirtyFormAlert,
  InputErrorMessage,
  Label,
  Modal,
  TextInput,
  Selector,
  TextArea,
  NumberInput,
} from 'shared/components';
import {
  isFeatureEnabled,
  getNumbersFromString,
  getCharsFromString,
  isValidDate,
  isFeatureAvailable,
} from 'shared/helpers';
import { platformFeatures, validityTypes } from 'shared/constants';
import {
  validateRequiredNumber,
  debouncedValidateRequiredNumber,
  validateDate,
  isValidJSON,
} from 'shared/validation';
import './styles.scss';
import { useTranslation } from 'react-i18next';

const ProductFeaturesEditorForm = ({
  closeCb,
  feature,
  onConfirm,
  license,
  disableFeatureExpiry,
}) => {
  const { t } = useTranslation();
  const isFeatureExpiryDateEnabled = isFeatureEnabled(
    platformFeatures.extra_feature_expiry_date,
  );
  const isMetadataFeatureEnabled = isFeatureEnabled(
    platformFeatures.extra_metadata,
  );
  const isLicenseActive = get(license, 'active');

  const isConsumptionType = get(feature, 'feature_type') === 'consumption';
  const isFloating = get(feature, 'is_floating');
  const isFloatingCloud = get(feature, 'is_floating_cloud');
  const isFloatingFeature = isFloating || isFloatingCloud;

  const consumptionPeriodOptions = [
    { label: t('Daily'), value: 'daily' },
    { label: t('Weekly'), value: 'weekly' },
    { label: t('Monthly'), value: 'monthly' },
    { label: t('Annually'), value: 'annually' },
  ];

  const periodOptions = [
    { label: t('Years'), value: 'y' },
    { label: t('Months'), value: 'm' },
    { label: t('Days'), value: 'd' },
  ];

  const notAvailableFloatingMessage = (
    <>
      {t('Floating features are unavailable in your current plan')}
      &nbsp;
      {t('Upgrade to use floating features')}
    </>
  );

  const getInitialSelectedDurationType = () => {
    const hasValidityPeriod = get(feature, 'expiry_date');
    if (hasValidityPeriod) {
      return validityTypes.period;
    }

    if (license && isLicenseActive) {
      return validityTypes.period;
    }
    return validityTypes.duration;
  };

  const [isLoading, setLoading] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [isDirtyFormDisplayed, setDirtyFormDisplay] = useState(false);
  const [maxConsumption, setMaxConsumption] = useState(
    get(feature, 'max_consumption', 0),
  );
  const [maxConsumptionError, setMaxConsumptionError] = useState('');
  const [allowUnlimitedConsumptions, setAllowUnlimitedConsumptions] = useState(
    get(feature, 'allow_unlimited_consumptions', false),
  );
  const [allowNegativeConsumptions, setAllowNegativeConsumptions] = useState(
    get(feature, 'allow_negative_consumptions', false),
  );
  const [allowExpiration, setAllowExpiration] = useState(
    !disableFeatureExpiry &&
      get(feature, 'valid_duration', get(feature, 'expiry_date', false)),
  );
  const [selectedDurationType, setSelectedDurationType] = useState(
    getInitialSelectedDurationType(),
  );
  const [validDurationValue, setValidDurationValue] = useState(
    getNumbersFromString(get(feature, 'valid_duration', '1y')),
  );
  const [validDurationPeriod, setValidDurationPeriod] = useState(
    getCharsFromString(get(feature, 'valid_duration', '1y')),
  );
  const [validDurationError, setValidDurationError] = useState('');
  const [expiryDate, setExpiryDate] = useState(get(feature, 'expiry_date', ''));
  const [expiryDateError, setExpiryDateError] = useState('');
  const [allowOverages, setAllowOverages] = useState(
    get(feature, 'allow_overages'),
  );
  const [maxOverages, setMaxOverages] = useState(
    get(feature, 'max_overages', 0),
  );
  const [maxOveragesError, setMaxOveragesError] = useState('');
  const [resetConsumption, setResetConsumption] = useState(
    get(feature, 'reset_consumption'),
  );
  const [resetConsumptionPeriod, setResetConsumptionPeriod] = useState(
    get(feature, 'consumption_period', 'daily'),
  );
  // metadata
  const [metadata, setMetadata] = useState(
    JSON.stringify(get(feature, 'metadata')),
  );
  const [metadataError, setMetadataError] = useState('');
  // floating
  const [floatingUsers, setFloatingUsers] = useState(
    get(feature, 'floating_users', 1),
  );
  const [floatingUsersError, setFloatingUsersError] = useState('');
  const [floatingTimeout, setFloatingTimeout] = useState(
    get(feature, 'floating_timeout', 120),
  );
  const [floatingTimeoutError, setFloatingTimeoutError] = useState('');
  // borrowing
  const [canBorrow, setCanBorrow] = useState(get(feature, 'can_borrow', false));
  const [maxBorrowTime, setMaxBorrowTime] = useState(
    get(feature, 'max_borrow_time', 0),
  );
  const [maxBorrowTimeError, setMaxBorrowTimeError] = useState('');

  const handleClose = () => {
    if (!isDirty) {
      return closeCb();
    }
    return setDirtyFormDisplay(true);
  };

  const validateValue = async (val, cb) => {
    setLoading(true);
    let errors;
    try {
      errors = await validateRequiredNumber(val);
      cb(errors);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
    if (errors) {
      return false;
    }
    return true;
  };

  const handleDateValidation = async () => {
    setLoading(true);
    let errors;
    try {
      const isRequired =
        allowExpiration && selectedDurationType === validityTypes.period;
      if (isRequired && !expiryDate) {
        errors = 'This field is required';
      } else if (expiryDate && !isValidDate(expiryDate)) {
        errors = 'Date must be in YYYY-MM-DD format';
      } else {
        errors = '';
      }
      setExpiryDateError(errors);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
    if (errors) {
      return false;
    }
    return true;
  };

  const validateNumericalValue = async (val, cb, includeZero) => {
    setLoading(true);
    let errors;
    try {
      errors = await validateRequiredNumber(val, includeZero);
      cb(errors);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
    if (errors) {
      return false;
    }
    return true;
  };

  const validateMetadata = async (val) => {
    setLoading(true);
    let errors = '';
    try {
      if (val && !isValidJSON(val)) {
        errors = 'Metadata must be a valid JSON object';
      }
      setMetadataError(errors);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
    if (errors) {
      return false;
    }
    return true;
  };

  const validateBorrowing = async () => {
    if (!canBorrow) {
      return true;
    }
    const isMaxBorrowTimeValid = await validateNumericalValue(
      maxBorrowTime,
      setMaxBorrowTimeError,
    );
    return isMaxBorrowTimeValid;
  };

  const handleAllowOveragesChange = (val) => {
    setDirty(true);
    setAllowOverages(val);
    setMaxOverages(get(feature, 'max_overages', 0));
    setMaxOveragesError('');
  };

  const handleMaxOveragesChange = (val) => {
    setDirty(true);
    setMaxOverages(val);
    debouncedValidateRequiredNumber(val).then((err) =>
      setMaxOveragesError(err),
    );
  };

  const handleResetConsumptionChange = (val) => {
    setDirty(true);
    setResetConsumption(val);
  };

  const handleResetConsumptionPeriodChange = (val) => {
    setDirty(true);
    setResetConsumptionPeriod(val);
  };

  const handleAllowUnlimitedConsumptions = (val) => {
    setDirty(true);
    setAllowUnlimitedConsumptions(val);
    setMaxConsumption(get(feature, 'max_consumption', 1));
    setMaxConsumptionError('');
  };

  const handleValidDurationValueChange = async (val) => {
    setDirty(true);
    setValidDurationValue(val);
    const validationErr = await debouncedValidateRequiredNumber(val);
    setValidDurationError(validationErr);
  };

  const handleValidDurationPeriodChange = (val) => {
    setDirty(true);
    setValidDurationPeriod(val);
  };

  const handleExpiryDateChange = async (val, isOptional = false) => {
    setDirty(true);
    if (!val && isOptional) {
      setExpiryDate(val);
      setExpiryDateError('');
      return true;
    }

    setExpiryDate(val);
    const validationErr = await validateDate(val);
    setExpiryDateError(validationErr);
    return true;
  };

  const handleCanBorrowChange = (val) => {
    setDirty(true);
    setCanBorrow(val);
    setMaxBorrowTime(get(feature, 'max_borrow_time', 0));
    setMaxBorrowTimeError('');
  };

  const handleMaxBorrowTimeChange = async (val) => {
    setDirty(true);
    setMaxBorrowTime(parseInt(val));
    const validationErr = await debouncedValidateRequiredNumber(val);
    setMaxBorrowTimeError(validationErr);
  };

  const isFormValid = async () => {
    const isFloatingUsersValid = isFloatingFeature
      ? await validateValue(floatingUsers, setFloatingUsersError)
      : true;
    const isFloatingTimeoutValid = isFloatingFeature
      ? await validateValue(floatingTimeout, setFloatingTimeoutError)
      : true;
    const isMaxConsumptionsValid = isConsumptionType
      ? await validateValue(maxConsumption, setMaxConsumptionError)
      : true;
    const isMaxOveragesValid =
      isConsumptionType && allowOverages
        ? await validateValue(maxOverages, setMaxOveragesError)
        : true;
    const isDateValid = await handleDateValidation();
    const isMetadataValid = await validateMetadata(metadata);
    const isBorrowValid = await validateBorrowing();

    return (
      isMaxConsumptionsValid &&
      isMaxOveragesValid &&
      isDateValid &&
      isMetadataValid &&
      isFloatingUsersValid &&
      isFloatingTimeoutValid &&
      isBorrowValid
    );
  };

  const getFloatingUsers = () => {
    if (!isFloating && !isFloatingCloud) {
      return undefined;
    }
    if (floatingUsers) {
      return Number(floatingUsers);
    }
    return undefined;
  };

  const getFloatingTimeout = () => {
    if (!isFloating && !isFloatingCloud) {
      return undefined;
    }
    if (floatingTimeout) {
      return Number(floatingTimeout);
    }
    return undefined;
  };

  const getValidDuration = () => {
    const isDurationValid = validDurationValue && validDurationPeriod;

    if (expiryDate) {
      return null;
    }

    if (isDurationValid) {
      return `${validDurationValue}${validDurationPeriod}`;
    }
    return undefined;
  };

  const getExpiryDate = () => {
    if (!allowExpiration || !expiryDate) {
      return null;
    }
    return expiryDate;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const floatingUsersNumber = getFloatingUsers();
    const floatingTimeoutValue = getFloatingTimeout();

    const data = {
      ...feature,
      max_consumption: Number(maxConsumption),
      allow_unlimited_consumptions: allowUnlimitedConsumptions,
      allow_negative_consumptions: allowNegativeConsumptions,
      allow_overages: allowOverages,
      max_overages: allowOverages ? Number(maxOverages) : undefined,
      reset_consumption: resetConsumption,
      consumption_period: resetConsumption ? resetConsumptionPeriod : undefined,
      expiry_date: allowExpiration ? getExpiryDate() : null,
      valid_duration: allowExpiration ? getValidDuration() : null,
      metadata: metadata ? JSON.parse(metadata) : {},
      is_floating: isFloating,
      is_floating_cloud: isFloatingCloud,
      floating_users: floatingUsersNumber,
      floating_timeout: floatingTimeoutValue || 120,
      can_borrow: canBorrow,
      max_borrow_time: maxBorrowTime,
    };

    const isValid = await isFormValid();
    if (!isValid || isLoading) {
      return false;
    }

    setLoading(true);

    onConfirm(data);
    return true;
  };

  return (
    <Modal
      confirmCb={handleSubmit}
      closeCb={handleClose}
      disabled={isLoading}
      title={t('Edit product feature')}
      size="sm"
    >
      <div className="ProductFeaturesEditorForm">
        <Label text={t('Feature name')} inputId="name" />
        <TextInput
          value={get(feature, 'name', get(feature, 'product_feature.name'))}
          handleChange={() => {}}
          disabled
        />
        {isConsumptionType && (
          <>
            <div>
              <Label text={t('Max consumption')} inputId="consumption" />
              <NumberInput
                handleChange={(val) => {
                  setDirty(true);
                  setMaxConsumption(val);
                  debouncedValidateRequiredNumber(val).then((err) =>
                    setMaxConsumptionError(err),
                  );
                }}
                value={maxConsumption}
                error={maxConsumptionError}
                min="1"
                max="2147483647"
                disabled={allowUnlimitedConsumptions}
              />
              <Checkbox
                label={t('Allow unlimited consumptions')}
                inputId="allow-unlimited-consumptions-checkbox"
                checked={allowUnlimitedConsumptions}
                handleChange={handleAllowUnlimitedConsumptions}
              />
            </div>
            <div className="form-row">
              <Checkbox
                label={t('Allow negative consumptions')}
                inputId="allow-negative-consumptions-checkbox"
                checked={allowNegativeConsumptions}
                handleChange={(val) => setAllowNegativeConsumptions(val)}
              />
            </div>
            <div className="form-row">
              <Checkbox
                label={t('Allow overages')}
                inputId="consumption-overages"
                checked={allowOverages}
                handleChange={handleAllowOveragesChange}
              />
            </div>
            {allowOverages && (
              <div className="form-row row-expanded expanded-consumption-row">
                <Label text={t('Max overages')} inputId="max-overages-input" />
                <NumberInput
                  handleChange={handleMaxOveragesChange}
                  value={maxOverages}
                  showErrorMsg={false}
                  error={maxOveragesError}
                  min="0"
                  max="2147483647"
                  id="max-overages-input"
                />
                <InputErrorMessage text={maxOveragesError} />
              </div>
            )}
            <div className="form-row">
              <Checkbox
                label={t('Reset consumption')}
                inputId="consumption-reset"
                checked={resetConsumption}
                handleChange={handleResetConsumptionChange}
              />
            </div>
            {resetConsumption && (
              <div className="form-row row-expanded">
                <Label text={t('Consumption period')} />
                <Selector
                  options={consumptionPeriodOptions}
                  value={resetConsumptionPeriod}
                  handleChange={handleResetConsumptionPeriodChange}
                />
              </div>
            )}
          </>
        )}
        <div className="time-limited-options-checkbox">
          <Checkbox
            label={t('Set feature expiration')}
            inputId="feature-expiration-checkbox"
            checked={allowExpiration}
            handleChange={(val) => setAllowExpiration(val)}
            disabled={!isFeatureExpiryDateEnabled || disableFeatureExpiry}
          />
        </div>
        {allowExpiration && (
          <div className="row time-limited-options">
            <div className="radio-btns">
              <RadioBtn
                name="time-limited-select"
                label={t('Valid from activation:')}
                inputId="valid_from"
                description={t(
                  'Select the validity duration of a feature, which begins the moment the license has been activated',
                )}
                value={validityTypes.duration}
                checked={selectedDurationType === validityTypes.duration}
                disabled={!!license && isLicenseActive}
                handleChange={(val) => {
                  setDirty(true);
                  setSelectedDurationType(val);
                  setExpiryDate('');
                  setExpiryDateError('');
                }}
              />
              <div>
                <div className="flex-row">
                  <NumberInput
                    handleChange={handleValidDurationValueChange}
                    value={validDurationValue}
                    min="0"
                    max="1000"
                    disabled={selectedDurationType === validityTypes.period}
                  />
                  <Selector
                    options={periodOptions}
                    value={validDurationPeriod}
                    handleChange={handleValidDurationPeriodChange}
                    disabled={selectedDurationType === validityTypes.period}
                  />
                </div>
                <InputErrorMessage text={validDurationError} />
              </div>
              <RadioBtn
                name="time-limited-select"
                inputId="valid_until"
                label={t('Expiry date')}
                description={t(
                  'Set the date in the future at which the features are set to expire',
                )}
                value={validityTypes.period}
                checked={selectedDurationType === validityTypes.period}
                handleChange={(val) => {
                  setDirty(true);
                  setSelectedDurationType(val);
                  setValidDurationValue(
                    getNumbersFromString(get(feature, 'valid_duration', '1y')),
                  );
                  setValidDurationPeriod(
                    getCharsFromString(get(feature, 'valid_duration', '1y')),
                  );
                  setValidDurationError('');
                }}
              />
              <DateInput
                id="release"
                handleChange={handleExpiryDateChange}
                value={expiryDate}
                error={expiryDateError}
                disabled={selectedDurationType === validityTypes.duration}
              />
            </div>
          </div>
        )}
        <div className="row">
          <Label inputId="metadata-input" text={t('Metadata JSON')} />
          <TextArea
            handleChange={(val) => {
              setDirty(true);
              setMetadata(val);
              const error =
                val && !isValidJSON(val)
                  ? 'Metadata must be a valid JSON object'
                  : '';
              setMetadataError(error);
            }}
            id="metadata-input"
            type="metadata"
            value={metadata}
            error={metadataError}
            rows="4"
            disabled={!isMetadataFeatureEnabled}
          />
        </div>
        {isFloatingFeature && (
          <>
            <div className="section-row max-simult-users">
              <Label text={t('Max simultaneous users')} />
              <NumberInput
                handleChange={(val) => {
                  setDirty(true);
                  setFloatingUsers(val);
                  debouncedValidateRequiredNumber(val).then((err) =>
                    setFloatingUsersError(err),
                  );
                }}
                value={floatingUsers}
                error={floatingUsersError}
                min="1"
                max="10000"
              />
            </div>
            <div className="section-row max-simult-users">
              <Label
                text={t('Floating timeout')}
                description={t(
                  'If the feature issued is a floating feature, the floating timeout is the time interval in minutes that the end users application will need to perform a license check in order to remain registered to the feature.',
                )}
              />
              <NumberInput
                handleChange={(val) => {
                  setDirty(true);
                  setFloatingTimeout(val);
                  debouncedValidateRequiredNumber(val).then((err) =>
                    setFloatingTimeoutError(err),
                  );
                }}
                value={floatingTimeout}
                error={floatingTimeoutError}
                min="1"
                max="2147483647"
              />
            </div>
            <Checkbox
              label={t('Can borrow feature')}
              inputId="can-borrow-feature"
              checked={canBorrow}
              handleChange={handleCanBorrowChange}
              featureEnabled={isFeatureEnabled(
                platformFeatures.extra_license_borrowing,
              )}
              featureAvailable={isFeatureAvailable(
                platformFeatures.extra_license_borrowing,
              )}
              notAvailableMessage={notAvailableFloatingMessage}
            />
            {canBorrow && (
              <div className="section-row row-expanded">
                <Label text={t('Max borrow time')} description={t('Hours')} />
                <NumberInput
                  handleChange={handleMaxBorrowTimeChange}
                  value={maxBorrowTime}
                  error={maxBorrowTimeError}
                  min="1"
                  max="2147483647"
                />
              </div>
            )}
          </>
        )}
      </div>
      {isDirtyFormDisplayed && (
        <DirtyFormAlert
          dirty={isDirty}
          closeAlert={() => setDirtyFormDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

ProductFeaturesEditorForm.propTypes = {
  closeCb: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  feature: PropTypes.object.isRequired,
  license: PropTypes.object,
  disableFeatureExpiry: PropTypes.bool,
};

ProductFeaturesEditorForm.defaultProps = {
  license: null,
  disableFeatureExpiry: false,
};

export default ProductFeaturesEditorForm;
